import React, {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { type GridRowId } from "@mui/x-data-grid-pro";
import { toast } from "react-toastify";
import { Box, Tooltip } from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import {
  BulkAnalyzeOptionsSection,
  INITIAL_BULK_ANALYZE_SELECTION,
  type BulkAnalyzeSelection,
} from "./BulkAnalyzeOptionsSection";
import { ConfirmBulkAcquisitionDialog } from "./ConfirmBulkAcquisitionDialog";
import { countHiddenPictures } from "../utils/bulkPictureRequestPayload";
import { selectCheckedItems } from "../utils/reuseSelection";
import { usesNew } from "../utils/acquirePolicy";
import { useBulkAcquireSubmit } from "../hooks/useBulkAcquireSubmit";
import {
  ALL_MATTERS_FIELDS,
  CHECKBOX_FIELDS,
  hasGenzaiOnlyRow,
} from "../utils/checkboxFields";
import { type CheckedIdsStore } from "../hooks/useCheckedIdsStore";
import { policyFromForm } from "../utils/bulkAcquireFormSchema";
import { type useBulkAcquireForm } from "../hooks/useBulkAcquireForm";
import {
  type BulkAcquisitionBreakdown,
  type BulkAcquisitionRequest,
  type BulkPropertyRow,
} from "../types";

interface BulkSubmitSectionProps {
  /** チェック状態ストア。購読はこの節に閉じ、チェック操作でグリッド側を再レンダリングさせない */
  store: CheckedIdsStore;
  /** 依頼対象になる全行。チェックは絞り込みをまたいで維持されるため、非表示行も対象になる */
  rows: BulkPropertyRow[] | null;
  /** 表示中の行IDの集合（絞り込み結果）。カウンタの非表示件数の算出に使う */
  visibleRowIds: Set<GridRowId> | null;
  form: ReturnType<typeof useBulkAcquireForm>;
}

/**
 * グリッドより下の送信フロー一式（解析オプション・一括取得ボタン・確認ダイアログ）
 */
export const BulkSubmitSection: React.FC<BulkSubmitSectionProps> = ({
  store,
  rows,
  visibleRowIds,
  form,
}) => {
  const checkedAll = useSyncExternalStore(store.subscribe, store.getAll);
  const { submit } = useBulkAcquireSubmit();

  const [confirmOpen, setConfirmOpen] = useState(false);
  // 確認モーダルを開くときに選別を1回だけ走らせ、送信するリクエストと画面に出す
  // 内訳・単価の対象列をまとめて凍結する。取得方法と経過日数はモーダルを開いた
  // 後もユーザー設定の再取得で書き換わりうるため、選別に使った値のまま送る
  const [confirmation, setConfirmation] = useState<{
    request: BulkAcquisitionRequest;
    breakdown: BulkAcquisitionBreakdown;
    checkedFields: string[];
  } | null>(null);
  const [analyze, setAnalyze] = useState<BulkAnalyzeSelection>(
    INITIAL_BULK_ANALYZE_SELECTION
  );
  // 解析オプション未選択時のエラーメッセージ（送信ブロック時のみ表示）
  const [allMattersOptionsError, setAllMattersOptionsError] = useState<
    string | null
  >(null);

  // チェックが1件以上ある取得対象（確認モーダルの単価表示と実行ボタン活性の判定に使う）
  const checkedFields = useMemo(
    () =>
      CHECKBOX_FIELDS.map(({ field }) => field).filter(
        (field) => (checkedAll[field]?.size ?? 0) > 0
      ),
    [checkedAll]
  );
  const hasCheckedRows = checkedFields.length > 0;

  const isOwnerInfoChecked = checkedFields.includes("ownerInfo");
  const isAllMattersChecked = ALL_MATTERS_FIELDS.some((field) =>
    checkedFields.includes(field)
  );

  // 対象のチェックが全て外れたら解析も外す（対象なしの解析依頼を防ぐ。既存画面と同じ挙動）
  useEffect(() => {
    setAnalyze((prev) => ({
      ...prev,
      withOwnerAnalyze: isOwnerInfoChecked ? prev.withOwnerAnalyze : false,
      withAllMattersAnalyze: isAllMattersChecked
        ? prev.withAllMattersAnalyze
        : false,
    }));
  }, [isOwnerInfoChecked, isAllMattersChecked]);

  const handleOpenConfirm = async (): Promise<void> => {
    const valid = await form.trigger();
    if (!valid) {
      return;
    }
    const genzaiIds = store.getField("genzai");
    const sintakuIds = store.getField("sintaku");
    const kyoutanIds = store.getField("kyoutan");
    if (hasGenzaiOnlyRow(genzaiIds, sintakuIds, kyoutanIds)) {
      toast.error("現在事項を取得する場合には共担か信託を選択してください");
      return;
    }
    if (
      analyze.withAllMattersAnalyze &&
      !analyze.allMattersAnalyzeOptions.includeTitleSection &&
      !analyze.allMattersAnalyzeOptions.includeKoku &&
      !analyze.allMattersAnalyzeOptions.includeOtsuku
    ) {
      setAllMattersOptionsError("解析オプションは最低1つ選択してください");
      return;
    }
    setAllMattersOptionsError(null);
    const values = form.getValues();
    const { identifyName, pastDays } = values;
    const policy = policyFromForm(values);
    const result = selectCheckedItems(
      rows ?? [],
      store.getAll(),
      policy,
      pastDays,
      new Date()
    );
    if (result.items.length === 0) {
      toast.error("取得対象になる組み合わせがありません");
      return;
    }
    setConfirmation({
      request: {
        identificationName: identifyName,
        allowNewAcquisition: usesNew(policy),
        // 経過日数を使わない条件では送らない（BE は未指定を制限なしとして記録する）。
        // 0 で送ると「0日以内」として記録され、入力欄にも出せない値が残る
        ...(!policy.ignoreWindow && { pastDays }),
        withOwnerAnalyze: analyze.withOwnerAnalyze,
        withAllMattersAnalyze: analyze.withAllMattersAnalyze,
        ...(analyze.withAllMattersAnalyze && analyze.allMattersAnalyzeOptions),
        items: result.items,
      },
      breakdown: {
        newPictureCount: result.newPictureCount,
        acquiredPictureCount: result.acquiredPictureCount,
        reusePictureCount: result.reusePictureCount,
        hiddenPictureCount: countHiddenPictures(
          result.items,
          result.rowsByPropertyCode,
          visibleRowIds
        ),
      },
      checkedFields,
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <BulkAnalyzeOptionsSection
        value={analyze}
        onChange={(value) => {
          setAnalyze(value);
          setAllMattersOptionsError(null);
        }}
        isOwnerInfoChecked={isOwnerInfoChecked}
        isAllMattersChecked={isAllMattersChecked}
        optionsError={allMattersOptionsError}
      />
      <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}>
        <Tooltip title={hasCheckedRows ? "" : "取得対象をチェックしてください"}>
          <span>
            <Button
              variant={ButtonVariantOption.Contained}
              label="一括取得する"
              disabled={!hasCheckedRows}
              onClick={() => {
                void handleOpenConfirm();
              }}
            />
          </span>
        </Tooltip>
      </Box>
      <ConfirmBulkAcquisitionDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        checkedFields={confirmation?.checkedFields ?? []}
        breakdown={confirmation?.breakdown ?? null}
        onConfirm={(cancelLoading) => {
          if (confirmation == null) {
            cancelLoading();
            return;
          }
          void submit(confirmation.request, cancelLoading);
        }}
      />
    </>
  );
};
